import {
    ClientPriceInput,
    resolveNettoMonatsumsatz,
    resolveTarifTier,
    resolveTeamProThreshold,
    TarifTier,
} from './clientPricing';
import { readPeoLetterFromMandantNr } from './soloMode';

/** Anzeige-Labels exakt für die Mandant-Nummer */
export type MandantTarifStatus = 'Solo' | 'TeamBase' | 'TeamPro' | 'TeamPro+';

export const TARIF_TIER_TO_STATUS: Record<TarifTier, MandantTarifStatus> = {
    solo: 'Solo',
    teamBase: 'TeamBase',
    teamPro: 'TeamPro',
    teamProPlus: 'TeamPro+',
};

/** Erkennung neues Format: `MA - …` */
export const isNewMandantNrFormat = (nr: string | null | undefined): boolean => {
    if (!nr || typeof nr !== 'string') return false;
    return /^\s*MA\s+-\s+/i.test(nr);
};

/** Coach-Initial C/M/J/N aus Name oder ID */
export const resolveCoachInitialLetter = (
    betreuerOrName: string | null | undefined,
    team?: any[]
): string => {
    if (!betreuerOrName) return '';
    let coachName = String(betreuerOrName);
    if (team && Array.isArray(team)) {
        const found = team.find((t: any) => t.id === betreuerOrName || t.name === betreuerOrName);
        if (found) coachName = found.name || found.firstName || coachName;
    }
    if (/jean/i.test(coachName)) return 'J';
    if (/nelly/i.test(coachName)) return 'N';
    if (/martina/i.test(coachName)) return 'M';
    if (/christian/i.test(coachName)) return 'C';
    const letter = coachName.trim().charAt(0).toUpperCase();
    if (['C', 'M', 'J', 'N'].includes(letter)) return letter;
    return '';
};

export interface ParsedMandantNr {
    core: string;
    coachInitial: string;
    tarifStatus: string;
    isNewFormat: boolean;
    isPeoFormat: boolean;
    raw: string;
}

/**
 * Kern `NN/YYYY` aus Alt (PEO-…, MA-I …, MAW …) und Neu (`MA - …`).
 * Crasht nicht bei unbekannten Strings. Normalisiert 01-2018 → 01/2018.
 */
export const extractCoreFromMandantNr = (nr: string | null | undefined): string => {
    if (!nr || typeof nr !== 'string') return '';
    let core = nr.toUpperCase().trim();

    try {
        // Überall MM/YYYY oder MM-YYYY oder MM.YYYY suchen (auch MAW 05/2022, MA-S-02.2017, "09 / 2018")
        const embedded = core.replace(/\s+/g, ' ').match(/(\d{1,2})\s*[./-]\s*(\d{4})/);
        if (embedded) {
            return `${String(embedded[1]).padStart(2, '0')}/${embedded[2]}`;
        }

        // Neues Format ohne erkennbares Datum im ersten Pass
        if (isNewMandantNrFormat(core)) {
            const m = core.match(/^MA\s+-\s+(.+?)\s+-\s+(?:[CMJN]\s+)?(.+)$/i);
            if (m && m[1]) {
                core = m[1].trim();
            } else {
                core = core.replace(/^MA\s+-\s+/i, '').trim();
                core = core.replace(/\s+-\s+[CMJN]?\s*(SOLO|TEAMBASE|TEAMPRO\+?|TEAMPRO).*$/i, '').trim();
            }
        } else {
            core = core.replace(/^(MA-I|MA-B|MA-S|MAW|PEO-T-|PEO-S-|PEO-B-|PEO-T|PEO-S|PEO-B|PEO-I|PEO)\s*/i, '');
            core = core.replace(/WIEDERHERGESTELLT/gi, '');
            core = core.replace(/[- ]+[A-Z]$/i, '');
            core = core.replace(/^[-\s]+|[-\s]+$/g, '');
        }

        if (core.length === 1 && /[A-Z]/i.test(core)) return '';

        core = core.replace(/(\d{2})[.-](\d{4})/g, '$1/$2');
        const again = core.match(/(\d{2})\/(\d{4})/);
        return again ? `${again[1]}/${again[2]}` : core.trim();
    } catch {
        return '';
    }
};

export const parseMandantNr = (nr: string | null | undefined): ParsedMandantNr => {
    const raw = (nr && typeof nr === 'string') ? nr.trim() : '';
    const empty: ParsedMandantNr = {
        core: '',
        coachInitial: '',
        tarifStatus: '',
        isNewFormat: false,
        isPeoFormat: false,
        raw,
    };
    if (!raw) return empty;

    try {
        const isNew = isNewMandantNrFormat(raw);
        const isPeo = /PEO-[BST]/i.test(raw);

        if (isNew) {
            // MA - CORE - I STATUS  |  MA - CORE - STATUS
            const withInitial = raw.match(
                /^MA\s+-\s+(.+?)\s+-\s+([CMJN])\s+(Solo|TeamBase|TeamPro\+|TeamPro)\s*$/i
            );
            if (withInitial) {
                return {
                    core: withInitial[1].replace(/(\d{2})\.(\d{4})/g, '$1/$2').trim(),
                    coachInitial: withInitial[2].toUpperCase(),
                    tarifStatus: normalizeTarifStatusLabel(withInitial[3]),
                    isNewFormat: true,
                    isPeoFormat: false,
                    raw,
                };
            }
            const withoutInitial = raw.match(
                /^MA\s+-\s+(.+?)\s+-\s+(Solo|TeamBase|TeamPro\+|TeamPro)\s*$/i
            );
            if (withoutInitial) {
                return {
                    core: withoutInitial[1].replace(/(\d{2})\.(\d{4})/g, '$1/$2').trim(),
                    coachInitial: '',
                    tarifStatus: normalizeTarifStatusLabel(withoutInitial[2]),
                    isNewFormat: true,
                    isPeoFormat: false,
                    raw,
                };
            }
        }

        let coachInitial = '';
        const suffix = raw.match(/[- ]+([CMJN])\s*$/i);
        if (suffix) coachInitial = suffix[1].toUpperCase();

        return {
            core: extractCoreFromMandantNr(raw),
            coachInitial,
            tarifStatus: '',
            isNewFormat: false,
            isPeoFormat: isPeo,
            raw,
        };
    } catch {
        return { ...empty, raw };
    }
};

const normalizeTarifStatusLabel = (s: string): MandantTarifStatus | string => {
    const t = String(s || '').trim();
    if (/^solo$/i.test(t)) return 'Solo';
    if (/^(teambase|team_base|team-base)$/i.test(t)) return 'TeamBase';
    if (/^(teampro\+|team_plus|team-plus|teamproplus)$/i.test(t)) return 'TeamPro+';
    if (/^(teampro|team)$/i.test(t)) return 'TeamPro';
    return t;
};

/** Sichtbares Badge-Label an der Mandantennummer. */
export const formatTarifBadgeLabel = (status: MandantTarifStatus): string => {
    if (status === 'Solo') return 'SOLO';
    if (status === 'TeamBase') return 'TEAM-BASE (Alt)';
    if (status === 'TeamPro+') return 'TEAM-PLUS';
    return 'TEAM';
};

export const tarifStatusFromTier = (tier: TarifTier): MandantTarifStatus =>
    TARIF_TIER_TO_STATUS[tier] || 'TeamPro';

export interface BuildMandantNrInput {
    core: string;
    coachInitial?: string | null;
    tarifStatus: MandantTarifStatus | string;
}

/**
 * Exaktes Spacing (Speicherformat, inkl. Tarif-Status für Migration/Parse):
 * mit Coach:   `MA - 02/2026 - C TeamPro`
 * ohne Coach:  `MA - 02/2026 - Solo`
 */
export const buildMandantNr = (input: BuildMandantNrInput): string => {
    const core = String(input.core || '')
        .trim()
        .replace(/(\d{2})\.(\d{4})/g, '$1/$2');
    const status = normalizeTarifStatusLabel(input.tarifStatus) || 'TeamPro';
    const initial = String(input.coachInitial || '')
        .trim()
        .toUpperCase()
        .charAt(0);

    if (!core) return '';

    if (initial && ['C', 'M', 'J', 'N'].includes(initial)) {
        return `MA - ${core} - ${initial} ${status}`;
    }
    return `MA - ${core} - ${status}`;
};

/** Vorabkunden / echte Alt-Nomenklatur (MA-I, MA-B, MA-S, MAW, PEO-I).
 *  Produktive System-IDs PEO-S / PEO-T / PEO-B sind KEIN Vorab — Badge bleibt aktiv. */
export const isVorabOrLegacyMandantNr = (nr: string | null | undefined): boolean => {
    if (!nr || typeof nr !== 'string') return false;
    const s = nr.trim();
    if (/^MA-I\b/i.test(s) || /^MA-B\b/i.test(s) || /^MA-S\b/i.test(s) || /^MAW\b/i.test(s)) return true;
    if (/^PEO-I\b/i.test(s)) return true;
    return false;
};

/**
 * Vorabkunden-Format (Anzeige + Speicher): `MA 31/2023-M`
 * — MA + Kern NN/YYYY + Coach-Initial (C/M/J/N). Kein Basic/Insider/Solo/Team-Suffix.
 */
export const buildVorabMandantNr = (
    core: string | null | undefined,
    coachInitial?: string | null
): string => {
    let c = String(core || '')
        .trim()
        .replace(/(\d{1,2})[.-](\d{4})/g, (_, m: string, y: string) => `${String(m).padStart(2, '0')}/${y}`);
    const matched = c.match(/^(\d{1,2})\/(\d{4})$/);
    if (matched) {
        c = `${String(matched[1]).padStart(2, '0')}/${matched[2]}`;
    }
    if (!c || !/^\d{2}\/\d{4}$/.test(c)) return '';

    const initial = String(coachInitial || '')
        .trim()
        .toUpperCase()
        .charAt(0);
    if (initial && ['C', 'M', 'J', 'N'].includes(initial)) {
        return `MA ${c}-${initial}`;
    }
    return `MA ${c}`;
};

/**
 * Normalisiert beliebige Alt-Nummern (MA-I, MAW, MA-B, MA-S, MA 31/2023, …)
 * auf das Vorab-Format `MA NN/YYYY-X`. Coach-Initial: Parameter oder Suffix aus der Altnummer.
 */
export const normalizeVorabMandantNr = (
    existingNr: string | null | undefined,
    coachInitial?: string | null
): string => {
    const raw = (existingNr && typeof existingNr === 'string') ? existingNr.trim() : '';
    if (!raw) {
        return buildVorabMandantNr('', coachInitial);
    }

    const parsed = parseMandantNr(raw);
    let core = parsed.core || extractCoreFromMandantNr(raw);

    if (!core || !/^\d{2}\/\d{4}$/.test(core)) {
        const loose = raw.toUpperCase().match(/(\d{1,2})[./-](\d{4})/);
        if (loose) {
            core = `${String(loose[1]).padStart(2, '0')}/${loose[2]}`;
        }
    }

    let initial = '';
    const fromParam = String(coachInitial || '')
        .trim()
        .toUpperCase()
        .charAt(0);
    if (fromParam && ['C', 'M', 'J', 'N'].includes(fromParam)) {
        initial = fromParam;
    } else if (parsed.coachInitial && ['C', 'M', 'J', 'N'].includes(parsed.coachInitial)) {
        initial = parsed.coachInitial;
    }

    return buildVorabMandantNr(core, initial);
};

/**
 * Gültiger Kalender-Monats-Kern MM/YYYY (01–12). Laufende Nummern wie 14/2026 sind kein Monat.
 */
export const isValidMonthYearCore = (core: string | null | undefined): boolean => {
    if (!core || typeof core !== 'string') return false;
    if (!/^\d{2}\/\d{4}$/.test(core.trim())) return false;
    const month = parseInt(core.trim().split('/')[0], 10);
    return month >= 1 && month <= 12;
};

/** Normalisiert diverse Datums-Strings zu MM/YYYY oder null. */
export const normalizeVertragsbeginnToMonthYear = (vb: unknown): string | null => {
    if (typeof vb !== 'string') return null;
    const t = vb.trim();
    if (!t) return null;
    if (/^\d{1,2}[./-]\d{4}$/.test(t)) {
        const norm = t.replace(/[.-]/g, '/');
        const [m, y] = norm.split('/');
        const core = `${String(m).padStart(2, '0')}/${y}`;
        return isValidMonthYearCore(core) ? core : null;
    }
    if (/^\d{4}-\d{2}/.test(t)) {
        const [y, m] = t.split('-');
        const core = `${m}/${y}`;
        return isValidMonthYearCore(core) ? core : null;
    }
    return null;
};

/**
 * Ersetzt MM/YYYY bzw. MM-YYYY / MM.YYYY in einer Mandantennummer durch einen neuen Kern.
 * Beispiel: MA-I-14-2026-N + 08/2026 → MA-I-08-2026-N
 */
export const replaceMonthYearInMandantNr = (
    nr: string | null | undefined,
    monthYear: string
): string => {
    const raw = (nr && typeof nr === 'string') ? nr.trim() : '';
    if (!isValidMonthYearCore(monthYear)) return raw;
    const [mm, yyyy] = monthYear.trim().split('/');
    if (!raw) return monthYear.trim();
    if (/(\d{2})[./-](\d{4})/.test(raw)) {
        return raw.replace(/(\d{2})([./-])(\d{4})/, `${mm}$2${yyyy}`);
    }
    return raw;
};

/**
 * Vertragsstart für CRM-Laufzeit: MM/YYYY.
 * Priorität:
 * 1) Bestandsmandant: gespeicherter vertragsbeginn (Tool-Einstieg — Nr-Kern ist nur Alt-Referenz)
 * 2) Mandanten-Nr.-Kern, wenn echter Kalendermonat 01–12 (Neukunden / manuelle Basisnummer)
 * 3) gespeicherter vertragsbeginn (Fallback)
 * Laufende Nummern wie 14/2026 in der ID werden nie als Monat gelesen.
 * createdAt wird bewusst nie verwendet (das ist nur die System-Anmeldung).
 */
export const resolveVertragsbeginnDisplay = (client: any): string | null => {
    if (!client) return null;
    try {
        const isBestand =
            client.isBestandskunde === true ||
            client.clientData?.isBestandskunde === true;

        const fromField = normalizeVertragsbeginnToMonthYear(
            client.clientData?.vertragsbeginn || client.vertragsbeginn
        );

        // Alt-Nr (z. B. 04/2025) ≠ Start in diesem Tool
        if (isBestand && fromField) return fromField;

        const nr = client.mandantNr || client.clientData?.mandantNr || '';
        const core = extractCoreFromMandantNr(nr);
        if (isValidMonthYearCore(core)) return core;

        if (fromField) return fromField;

        return null;
    } catch {
        return null;
    }
};

/**
 * Anzeige ohne Tarif-Suffix — Status gehört nur ins farbige Badge.
 * Vorab / Alt (MA-I, MAW, MA-B, MA-S, …): live auf `MA NN/YYYY-X` normalisieren.
 * `MA - 02/2027 - M TeamPro` → `MA - 02/2027 - M`
 */
export const formatMandantNrDisplay = (
    nr: string | null | undefined,
    coachInitial?: string | null
): string => {
    const raw = (nr && typeof nr === 'string') ? nr.trim() : '';
    if (!raw) return '';
    try {
        // Vorab / Alt-Müll → sauberes Anzeigeformat
        if (
            isVorabOrLegacyMandantNr(raw) ||
            /^MA\s+\d{1,2}\s*[./-]\s*\d{4}/i.test(raw) ||
            /^MA-[ISTBW]/i.test(raw) ||
            /^PEO-I\b/i.test(raw)
        ) {
            const clean = normalizeVorabMandantNr(raw, coachInitial);
            if (clean) return clean;
            return raw;
        }
        const parsed = parseMandantNr(raw);
        if (parsed.isNewFormat && parsed.core) {
            if (parsed.coachInitial) return `MA - ${parsed.core} - ${parsed.coachInitial}`;
            return `MA - ${parsed.core}`;
        }
        // Schon sauberes Vorab-Format `MA 31/2023-M`
        if (/^MA\s+\d{2}\/\d{4}(-[CMJN])?$/i.test(raw)) {
            return normalizeVorabMandantNr(raw, coachInitial) || raw;
        }
        return raw
            .replace(/\s+(Solo|TeamBase|TeamPro\+|TeamPro)\s*$/i, '')
            .trim();
    } catch {
        return raw;
    }
};

export interface MigrateMandantNrInput {
    existingNr?: string | null;
    isLegacy?: boolean;
    core?: string;
    coachInitial?: string | null;
    client?: any;
    settings?: ClientPriceInput['settings'];
    forceSoloTarif?: boolean;
    hasEmployeeEval?: boolean;
    isSolo?: boolean;
    nettoMonatsumsatz?: number | null;
    lizenzTyp?: string;
}

/**
 * Vorabkunden (isLegacy): Nummer unverändert zurück.
 * Sonst: neues Format aus Core + Coach + Phase-1-Tarif.
 */
export const migrateMandantNrIfNeeded = (input: MigrateMandantNrInput): string => {
    if (input.isLegacy === true) {
        return (input.existingNr || '').trim();
    }

    const existing = (input.existingNr || '').trim();
    let core = (input.core || '').trim();
    if (!core) {
        core = extractCoreFromMandantNr(existing);
    }
    if (!core) return existing;

    core = core.replace(/(\d{2})\.(\d{4})/g, '$1/$2');

    const parsed = parseMandantNr(existing);
    const coachInitial =
        (input.coachInitial !== undefined && input.coachInitial !== null
            ? String(input.coachInitial).trim()
            : '') || parsed.coachInitial || '';

    // Alt PEO ohne Flags: Buchstabe → Tarif-Status ableiten
    const peoLetter = readPeoLetterFromMandantNr(existing);
    let isSolo = input.isSolo;
    let hasEmployeeEval = input.hasEmployeeEval;
    if (isSolo === undefined && peoLetter === 'S') isSolo = true;
    if (isSolo === undefined && (peoLetter === 'T' || peoLetter === 'B')) isSolo = false;
    if (hasEmployeeEval === undefined && peoLetter === 'T') hasEmployeeEval = true;
    if (hasEmployeeEval === undefined && peoLetter === 'B') hasEmployeeEval = false;

    const tier = resolveTarifTier({
        settings: input.settings,
        client: input.client,
        forceSoloTarif: input.forceSoloTarif,
        isSolo,
        hasEmployeeEval,
        wantsEvaluation: hasEmployeeEval,
        nettoMonatsumsatz: input.nettoMonatsumsatz,
        lizenzTyp: input.lizenzTyp,
        mandantNr: existing,
    });

    return buildMandantNr({
        core,
        coachInitial: coachInitial || null,
        tarifStatus: tarifStatusFromTier(tier),
    });
};

/** Tailwind-Klassen für Status-Badges — Light: dunkler Text, Dark: Gold/Neon */
export const TARIF_BADGE_CLASS: Record<MandantTarifStatus, string> = {
    Solo: 'bg-purple-200/80 text-purple-900 border-purple-400/60 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/30',
    TeamBase: 'bg-blue-200/80 text-blue-900 border-blue-400/60 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30',
    TeamPro: 'bg-[#E6C299]/45 text-[#5C3D1F] border-[#8D6435]/55 dark:bg-[#E6C299]/10 dark:text-[#E6C299] dark:border-[#E6C299]/30',
    'TeamPro+': 'bg-[#E6C299]/45 text-[#5C3D1F] border-[#8D6435]/55 dark:bg-[#E6C299]/10 dark:text-[#E6C299] dark:border-[#E6C299]/30',
};

export interface ResolveTarifBadgeInput {
    mandantNr?: string | null;
    client?: any;
    settings?: ClientPriceInput['settings'];
    forceSoloTarif?: boolean;
    isSolo?: boolean;
    hasEmployeeEval?: boolean;
    nettoMonatsumsatz?: number | null;
    lizenzTyp?: string;
    isLegacy?: boolean;
}

/** TeamPro ↔ TeamPro+ anhand Live-Umsatz (Schwelle aus Globaldaten). */
const applyTeamProPlusFromUmsatz = (
    base: 'TeamPro' | 'TeamPro+',
    input: ResolveTarifBadgeInput
): MandantTarifStatus => {
    const client = input.client;
    const umsatz = resolveNettoMonatsumsatz({
        settings: input.settings,
        client,
        nettoMonatsumsatz: input.nettoMonatsumsatz,
    });
    const threshold = resolveTeamProThreshold(input.settings?.global) ?? 0;
    if (umsatz !== null && Number.isFinite(umsatz) && threshold > 0) {
        return umsatz >= threshold ? 'TeamPro+' : 'TeamPro';
    }
    const flaggedPlus =
        client?.isTeamProPlus === true ||
        client?.clientData?.isTeamProPlus === true ||
        client?.insiderTeamleaderPlusChecked === true ||
        client?.clientData?.insiderTeamleaderPlusChecked === true;
    if (flaggedPlus) return 'TeamPro+';
    return base === 'TeamPro+' ? 'TeamPro+' : 'TeamPro';
};

const buildTarifBadgeResult = (status: MandantTarifStatus) => ({
    status,
    label: formatTarifBadgeLabel(status),
    className: `px-2 py-1 rounded text-[9px] font-bold tracking-widest border ${TARIF_BADGE_CLASS[status]}`,
});

/**
 * Status aus Flags / PEO / Nummer → Label + Badge-Klassen.
 * Vorabkunden: Badge aus Flags/packageTier (Nummer bleibt ohne Solo/Team-Suffix).
 * Alle übrigen (PEO-S/T/B, MA - …, Flags) → Solo / TeamBase / TeamPro(+).
 */
export const resolveTarifBadgeDisplay = (
    input: ResolveTarifBadgeInput
): { status: MandantTarifStatus; className: string; label: string } | null => {
    try {
        const nr = input.mandantNr || input.client?.mandantNr || input.client?.clientData?.mandantNr || '';
        const legacyFlag =
            input.isLegacy === true ||
            input.client?.isLegacy === true ||
            input.client?.clientData?.isLegacy === true;

        // Solo / TeamBase: Root sticky. TeamPro / TeamPro+: Live-Umsatz → Plus (Schwelle)
        const clientForRoot = input.client;
        const rootRaw =
            clientForRoot?.packageTier ||
            clientForRoot?.tarifStatus ||
            clientForRoot?.clientData?.tarifStatus ||
            clientForRoot?.clientData?.packageTier ||
            '';
        const rootStatus = normalizeTarifStatusLabel(String(rootRaw));

        // Vorabkunden: Badge nur aus gespeichertem Status / Flags — nie aus der Nummer
        if (legacyFlag || isVorabOrLegacyMandantNr(nr)) {
            if (rootStatus === 'Solo' || rootStatus === 'TeamBase') {
                return buildTarifBadgeResult(rootStatus);
            }
            if (rootStatus === 'TeamPro' || rootStatus === 'TeamPro+') {
                const status = applyTeamProPlusFromUmsatz(rootStatus, input);
                return buildTarifBadgeResult(status);
            }
            let isSoloFlag = input.isSolo;
            let hasEval = input.hasEmployeeEval;
            if (isSoloFlag === undefined && input.client) {
                if (input.client.isSolo === true || input.client.clientData?.isSolo === true) isSoloFlag = true;
                if (input.client.isSolo === false || input.client.clientData?.isSolo === false) isSoloFlag = false;
            }
            if (hasEval === undefined && input.client) {
                if (input.client.hasEmployeeEval === true || input.client.clientData?.hasEmployeeEval === true) {
                    hasEval = true;
                }
                if (input.client.hasEmployeeEval === false || input.client.clientData?.hasEmployeeEval === false) {
                    hasEval = false;
                }
            }
            if (input.forceSoloTarif === true || input.client?.forceSoloTarif === true || input.client?.clientData?.forceSoloTarif === true) {
                isSoloFlag = true;
            }
            if (isSoloFlag === true) {
                return buildTarifBadgeResult('Solo');
            }
            if (hasEval === true) {
                const status = applyTeamProPlusFromUmsatz('TeamPro', input);
                return buildTarifBadgeResult(status);
            }
            // TeamBase nur wenn bewusst gesetzt (Flags + kein Solo) — nicht aus leerem Default
            const lizenz =
                input.lizenzTyp ||
                input.client?.lizenzDetails?.typ ||
                input.client?.clientData?.lizenzDetails?.typ ||
                '';
            if (isSoloFlag === false && hasEval === false && (lizenz === 'basic' || lizenz === 'insider')) {
                return buildTarifBadgeResult('TeamBase');
            }
            // Noch kein Tarif gesetzt → kein Badge
            return null;
        }

        if (rootStatus === 'Solo' || rootStatus === 'TeamBase') {
            return buildTarifBadgeResult(rootStatus);
        }
        if (rootStatus === 'TeamPro' || rootStatus === 'TeamPro+') {
            const status = applyTeamProPlusFromUmsatz(rootStatus, input);
            return buildTarifBadgeResult(status);
        }

        const peoLetter = readPeoLetterFromMandantNr(nr);
        const parsedNr = parseMandantNr(nr);
        const nrStatus = parsedNr.isNewFormat && parsedNr.tarifStatus
            ? normalizeTarifStatusLabel(parsedNr.tarifStatus)
            : null;

        // Neues MA-Format: Status in der Nummer — TeamPro kann per Umsatz/Flag zu TeamPro+ upgraden
        if (
            nrStatus === 'Solo' ||
            nrStatus === 'TeamBase' ||
            nrStatus === 'TeamPro' ||
            nrStatus === 'TeamPro+'
        ) {
            let status: MandantTarifStatus = nrStatus;
            if (nrStatus === 'TeamPro' || nrStatus === 'TeamPro+') {
                status = applyTeamProPlusFromUmsatz(nrStatus, input);
            }
            return buildTarifBadgeResult(status);
        }

        let isSolo = input.isSolo;
        let hasEmployeeEval = input.hasEmployeeEval;
        if (isSolo === undefined && input.client) {
            if (input.client.isSolo === true || input.client.clientData?.isSolo === true) isSolo = true;
            if (input.client.isSolo === false || input.client.clientData?.isSolo === false) isSolo = false;
        }
        if (hasEmployeeEval === undefined && input.client) {
            if (input.client.hasEmployeeEval === true || input.client.clientData?.hasEmployeeEval === true) {
                hasEmployeeEval = true;
            }
            if (input.client.hasEmployeeEval === false || input.client.clientData?.hasEmployeeEval === false) {
                hasEmployeeEval = false;
            }
        }
        // Team-Signal in der Nummer schlägt stale isSolo (ohne bewussten forceSolo-Override)
        const teamSignalFromNr =
            peoLetter === 'T' ||
            peoLetter === 'B';
        if (teamSignalFromNr) {
            if (isSolo === true) isSolo = false;
        }
        if (isSolo === undefined && peoLetter === 'S') isSolo = true;
        if (isSolo === undefined && (peoLetter === 'T' || peoLetter === 'B')) isSolo = false;
        if (hasEmployeeEval === undefined && peoLetter === 'T') hasEmployeeEval = true;
        if (hasEmployeeEval === undefined && peoLetter === 'B') hasEmployeeEval = false;

        const forceSoloFromClient = !!(input.client?.forceSoloTarif || input.client?.clientData?.forceSoloTarif);
        const forceSolo =
            input.forceSoloTarif === true || (input.forceSoloTarif === undefined && forceSoloFromClient);

        // Team in der Nummer schlägt Solo-Zwang (stale Flags / Override); Solo-Override schreibt Nummer neu
        const effectiveForceSolo = teamSignalFromNr ? false : forceSolo;

        const status = tarifStatusFromTier(resolveTarifTier({
            settings: input.settings,
            client: input.client,
            forceSoloTarif: effectiveForceSolo,
            isSolo: effectiveForceSolo ? true : isSolo,
            hasEmployeeEval,
            wantsEvaluation: hasEmployeeEval,
            nettoMonatsumsatz:
                input.nettoMonatsumsatz ??
                input.client?.aggregatedNettoMonatsumsatz ??
                input.client?.clientData?.aggregatedNettoMonatsumsatz ??
                input.client?.nettoMonatsumsatz ??
                input.client?.clientData?.nettoMonatsumsatz ??
                null,
            lizenzTyp:
                input.lizenzTyp ||
                input.client?.lizenzDetails?.typ ||
                input.client?.clientData?.lizenzDetails?.typ,
            mandantNr: nr,
        }));

        if (!status) return null;

        // Nach Flag-Auflösung: Live-Umsatz schlägt sticky Plus-Flag
        let finalStatus: MandantTarifStatus = status;
        if (status === 'TeamPro' || status === 'TeamPro+') {
            finalStatus = applyTeamProPlusFromUmsatz(status, input);
        }

        return buildTarifBadgeResult(finalStatus);
    } catch {
        return null;
    }
};
